import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactPlayer from "react-player";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Divider,
  Container,
  IconButton,
  Tooltip,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import MenuBookIcon from "@mui/icons-material/MenuBook";

const CoursePage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [watchedVideos, setWatchedVideos] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Improved color palette
  const theme = {
    primary: "#1a5f7a",
    secondary: "#57c1e9",
    background: "#f0f7fa",
    accent: "#ffa62b",
    dark: "#2c3e50",
    light: "#ffffff",
    success: "#4caf50",
    hover: "rgba(87, 193, 233, 0.1)",
  };

  // Function to select the first video based on watched status
  const selectDefaultVideo = (courseData, watchedList) => {
    if (!courseData.videos || Object.keys(courseData.videos).length === 0) {
      return null;
    }

    const videoList = Object.values(courseData.videos);

    // First, try to find the first unwatched video
    const firstUnwatched = videoList.find(video => !watchedList.includes(video.url));

    // If there's an unwatched video, select it; otherwise, select the first video
    return firstUnwatched || videoList[0];
  };

  useEffect(() => {
    // Fetch both course data and watched videos simultaneously
    const fetchCourseData = fetch(`https://dun.vydeo.xyz/iter3/course/${courseId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }).then((res) => res.json());

    const fetchWatchedVideos = fetch("https://dun.vydeo.xyz/iter3/get_watched", {
      credentials: "include",
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    }).then((res) => res.json());

    // Wait for both requests to complete
    Promise.all([fetchCourseData, fetchWatchedVideos])
      .then(([courseData, watchedData]) => {
        console.log("Raw watched data from API:", watchedData);

        // Handle different possible data structures
        let flatWatched = [];
        if (Array.isArray(watchedData)) {
          // If it's an array of arrays like [[url1], [url2]]
          if (watchedData.length > 0 && Array.isArray(watchedData[0])) {
            flatWatched = watchedData.map((v) => v[0]);
          }
          // If it's an array of objects or strings
          else {
            flatWatched = watchedData.map((v) =>
              typeof v === 'object' ? (v.video_url || v.url || v[0]) : v
            );
          }
        }

        // Set course and watched videos
        setCourse(courseData);
        setWatchedVideos(flatWatched);

        // Select default video based on watched status
        const defaultVideo = selectDefaultVideo(courseData, flatWatched);
        setSelectedVideo(defaultVideo);

        console.log("Course data:", courseData);
        console.log("Processed watched videos:", flatWatched);
        console.log("Selected video:", defaultVideo);
        console.log("Course videos for comparison:", courseData.videos ? Object.values(courseData.videos).map(v => v.url) : []);

        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setLoading(false);
      });
  }, [courseId]);

  const handleVideoStart = async (url) => {
    console.log("Video started:", url);
    console.log("Currently watched videos:", watchedVideos);
    console.log("Is already watched?", watchedVideos.includes(url));

    if (!watchedVideos.includes(url)) {
      try {
        console.log("Attempting to log video as watched...");
        const requestBody = { course_id: parseInt(courseId), video_url: url };
        console.log("Request body:", requestBody);

        const res = await fetch("https://dun.vydeo.xyz/iter3/log_watched", {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        console.log("Response status:", res.status);
        const responseData = await res.text();
        console.log("Response data:", responseData);

        if (res.ok) {
          setWatchedVideos((prev) => {
            const updated = [...prev, url];
            console.log("Updated watched videos:", updated);
            return updated;
          });
          setSnackbarMessage("Video marked as watched!");
          setSnackbarOpen(true);
        } else {
          console.error("Failed to log video - HTTP", res.status);
          setSnackbarMessage(`Failed to log video - ${res.status}`);
          setSnackbarOpen(true);
        }
      } catch (err) {
        console.error("Error logging video:", err);
        setSnackbarMessage("Failed to log video.");
        setSnackbarOpen(true);
      }
    } else {
      console.log("Video already marked as watched, skipping...");
    }
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor={theme.background}
      >
        <CircularProgress sx={{ color: theme.primary }} />
      </Box>
    );
  if (!course)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor={theme.background}
      >
        <Typography variant="h4" align="center" color={theme.dark}>
          Course not found
        </Typography>
      </Box>
    );

  const calculateProgress = () => {
    const totalVideos = Object.values(course.videos).length;
    const watched = Object.values(course.videos).filter((video) =>
      watchedVideos.includes(video.url)
    ).length;
    return Math.round((watched / totalVideos) * 100);
  };

  const progress = calculateProgress();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        sx={{
          backgroundColor: theme.background,
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="xl">
          {/* Header */}
          <Box
            sx={{
              pt: 4,
              pb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h4"
                component="h1"
                fontWeight="700"
                color={theme.primary}
                sx={{
                  borderLeft: `5px solid ${theme.accent}`,
                  pl: 2,
                  mb: 1,
                }}
              >
                {course.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} ml={2}>
                <Chip
                  icon={<MenuBookIcon />}
                  label={course.format}
                  sx={{
                    bgcolor: theme.primary,
                    color: "white",
                    fontWeight: "500",
                  }}
                />
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${progress}% Complete`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </motion.div>
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={4}
            mb={4}
            sx={{ position: "relative" }}
          >
            {/* Video Section */}
            <Box flex="2 1 650px" minWidth="300px">
              {selectedVideo && (
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.7 }}
                >
                  <Paper
                    elevation={4}
                    sx={{
                      position: "relative",
                      p: 3,
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: theme.light,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="600"
                      color={theme.dark}
                      sx={{
                        mb: 2,
                        pb: 1,
                        borderBottom: `2px solid ${theme.accent}`,
                      }}
                    >
                      {selectedVideo.title}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        height: "450px",
                        marginBottom: "20px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                      }}
                    >
                      <ReactPlayer
                        url={selectedVideo.url}
                        controls
                        width="100%"
                        height="100%"
                        onStart={() => handleVideoStart(selectedVideo.url)}
                        style={{ borderRadius: "8px" }}
                      />
                    </Box>
                    {watchedVideos.includes(selectedVideo.url) && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 1,
                          mb: 2,
                          p: 1,
                          bgcolor: "rgba(76, 175, 80, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        <Typography color="success.main">
                          You've watched this lesson
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </motion.div>
              )}
            </Box>

            {/* Sidebar with lessons */}
            <Box flex="1 1 300px" minWidth="300px">
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: theme.light,
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color={theme.dark}
                    sx={{
                      mb: 2,
                      pb: 1,
                      borderBottom: `2px solid ${theme.accent}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <PlayCircleOutlineIcon />
                    Course Content
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {Object.entries(course.videos).map(([id, video], index) => {
                      const isWatched = watchedVideos.includes(video.url);
                      const isSelected =
                        selectedVideo && selectedVideo.url === video.url;
                      return (
                        <motion.div
                          key={id}
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Paper
                            elevation={isSelected ? 2 : 0}
                            sx={{
                              mb: 2,
                              borderRadius: 2,
                              border: isSelected
                                ? `2px solid ${theme.primary}`
                                : "1px solid rgba(0,0,0,0.08)",
                              bgcolor: isSelected
                                ? theme.hover
                                : theme.light,
                              transition: "all 0.2s ease",
                              position: "relative",
                              overflow: "hidden",
                              "&:hover": {
                                boxShadow:
                                  "0 4px 8px rgba(0,0,0,0.1)",
                              },
                              "&::before": isSelected
                                ? {
                                  content: '""',
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "4px",
                                  bgcolor: theme.primary,
                                }
                                : {},
                            }}
                          >
                            <ListItem
                              button
                              onClick={() => setSelectedVideo(video)}
                              sx={{
                                py: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    bgcolor: isWatched
                                      ? theme.success
                                      : "rgba(0,0,0,0.08)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    mr: 2,
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {isWatched ? (
                                    <CheckCircleIcon
                                      sx={{ fontSize: 18 }}
                                    />
                                  ) : (
                                    index + 1
                                  )}
                                </Box>
                                <ListItemText
                                  primary={video.title}
                                  primaryTypographyProps={{
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isWatched ? "text.secondary" : "text.primary",
                                  }}
                                />
                              </Box>
                            </ListItem>
                          </Paper>
                        </motion.div>
                      );
                    })}
                  </List>
                </Paper>
              </motion.div>
            </Box>
          </Box>

          {/* Course Description */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                mb: 6,
                bgcolor: theme.light,
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="h5"
                fontWeight="700"
                color={theme.primary}
                sx={{ mb: 3 }}
              >
                About This Course
              </Typography>
              <Typography
                paragraph
                sx={{ lineHeight: 1.8, fontSize: "1.05rem", mb: 3 }}
              >
                {course.desc}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  justifyContent: "space-between",
                }}
              >
                <Box flex="1 1 300px" minWidth="250px">
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color={theme.dark}
                    sx={{ mb: 2 }}
                  >
                    Course Format
                  </Typography>
                  <Typography
                    sx={{
                      p: 2,
                      borderLeft: `4px solid ${theme.accent}`,
                      bgcolor: "rgba(255, 166, 43, 0.08)",
                      borderRadius: "0 4px 4px 0",
                    }}
                  >
                    {course.format}
                  </Typography>
                </Box>

                <Box flex="1 1 300px" minWidth="250px">
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color={theme.dark}
                    sx={{ mb: 2 }}
                  >
                    Prerequisites
                  </Typography>
                  <Typography
                    sx={{
                      p: 2,
                      borderLeft: `4px solid ${theme.secondary}`,
                      bgcolor: "rgba(87, 193, 233, 0.08)",
                      borderRadius: "0 4px 4px 0",
                    }}
                  >
                    {course.prerequisites}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color={theme.dark}
                  sx={{ mb: 2 }}
                >
                  Topics Covered
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  {course.topics.map((point, index) => (
                    <Chip
                      key={index}
                      label={point}
                      sx={{
                        bgcolor: "rgba(26, 95, 122, 0.08)",
                        border: `1px solid ${theme.primary}`,
                        color: theme.primary,
                        fontWeight: 500,
                        py: 0.5,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Paper>
          </motion.div>

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pb: 8,
            }}
          >
            <Button
              variant="contained"
              startIcon={<NavigateBeforeIcon />}
              disabled={parseInt(courseId) === 1}
              onClick={() => {
                const prevId = parseInt(courseId) - 1;
                window.location.href = `/course/${prevId}`;
              }}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: "bold",
                borderRadius: 2,
                bgcolor: theme.dark,
                "&:hover": {
                  bgcolor: "#1e2b38",
                },
                transition: "all 0.3s ease",
              }}
            >
              Previous Course
            </Button>

            <Button
              variant="contained"
              endIcon={<NavigateNextIcon />}
              disabled={parseInt(courseId) === 3} // Adjust if you have more courses
              onClick={() => {
                const nextId = parseInt(courseId) + 1;
                window.location.href = `/course/${nextId}`;
              }}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: "bold",
                borderRadius: 2,
                bgcolor: theme.primary,
                "&:hover": {
                  bgcolor: "#13475d",
                },
                transition: "all 0.3s ease",
              }}
            >
              Next Course
            </Button>
          </Box>
        </Container>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </motion.div>
  );
};

export default CoursePage;